import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Modal, Input, message as toaster } from 'antd';
import { EyeOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons';
import { handlePoRApproval, POR_DOC_ENTITY } from 'store/approvalSlice';
import { getSignedUrl } from 'store/commonSlice';

const { TextArea } = Input;

const ModalWrapper = styled.div`
	background: #fff;
	border-radius: 8px;
	overflow: hidden;
`;

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 24px;
	border-bottom: 1px solid #eee;
`;

const HeaderTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #18405d;
`;

const CloseButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	font-size: 18px;
	color: #707070;
`;

const PreviewPane = styled.div`
	margin: 24px;
	border: 1px solid #eee;
	border-radius: 8px;
	min-height: 360px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	color: #8a8a8a;
	background: #f4f4f4;
	gap: 8px;
	overflow: hidden;
`;

const PreviewImage = styled.img`
	max-width: 100%;
	max-height: 60vh;
	object-fit: contain;
`;

const PreviewFrame = styled.iframe`
	width: 100%;
	height: 60vh;
	border: none;
`;

const FooterButtons = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid #eee;
`;

const ActionButton = styled.button`
	border: 1px solid ${(p) => p.borderColor || '#c6c6c8'};
	color: ${(p) => p.borderColor || '#333'};
	background: #fff;
	border-radius: 6px;
	padding: 8px 28px;
	font-weight: 600;
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

/* Reason for Rejection modal */
const RejectModalContent = styled.div`
	padding: 24px;
`;

const RejectModalTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	margin-bottom: 16px;
`;

const RejectLabel = styled.div`
	font-size: 13px;
	color: #707070;
	margin-bottom: 6px;
`;

const RejectModalButtons = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 20px;
`;

const CancelButton = styled.button`
	border: 1px solid #ff3b30;
	color: #ff3b30;
	background: #fff;
	border-radius: 6px;
	padding: 6px 20px;
	cursor: pointer;
`;

const SubmitButton = styled.button`
	border: none;
	color: #fff;
	background: #18405d;
	border-radius: 6px;
	padding: 6px 20px;
	cursor: pointer;
`;

const isPdf = (url) => (url || '').split('?')[0].toLowerCase().endsWith('.pdf');

export default function PoRViewerModal({ porId, documentKey, data, onActionComplete }) {
	const dispatch = useDispatch();
	const [open, setOpen] = useState(false);
	const [docUrl, setDocUrl] = useState(null);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [loading, setLoading] = useState(false);

	const openModal = () => {
		setOpen(true);
		if (documentKey) {
			dispatch(
				getSignedUrl({
					url_type: 'view',
					data: { entity: POR_DOC_ENTITY, keys: [documentKey] },
				})
			).then((res) => setDocUrl(res?.data?.[0]?.url || null));
		}
	};

	const handleCancel = () => {
		setOpen(false);
	};

	const finish = (msg) => {
		toaster.success(msg);
		setLoading(false);
		setShowRejectModal(false);
		setRejectionReason('');
		setOpen(false);
		if (onActionComplete) onActionComplete();
	};

	const handleApprove = () => {
		setLoading(true);
		dispatch(handlePoRApproval({ por_id: porId, action: 'approve' }))
			.then(() => finish('Proof of Request approved successfully.'))
			.catch(() => setLoading(false));
	};

	const handleRejectSubmit = () => {
		if (!rejectionReason) {
			toaster.error('Please enter a reason for rejection');
			return;
		}
		setLoading(true);
		dispatch(
			handlePoRApproval({
				por_id: porId,
				action: 'reject',
				rejection_reason: rejectionReason,
			})
		)
			.then(() => finish('Proof of Request rejected.'))
			.catch(() => setLoading(false));
	};

	const isPending = data?.status === 'pending';

	return (
		<>
			<button
				style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}
				type="button"
				onClick={openModal}
			>
				<EyeOutlined style={{ fontSize: '16px' }} />
			</button>

			<Modal
				open={open}
				style={{ top: 20 }}
				width={760}
				onCancel={handleCancel}
				footer={null}
				closable={false}
				bodyStyle={{ padding: 0 }}
			>
				<ModalWrapper>
					<ModalHeader>
						<HeaderTitle>Proof of Request Preview</HeaderTitle>
						<CloseButton onClick={handleCancel}>
							<CloseOutlined />
						</CloseButton>
					</ModalHeader>

					<PreviewPane>
						{docUrl && isPdf(docUrl) && (
							<PreviewFrame title="Proof of Request" src={docUrl} />
						)}
						{docUrl && !isPdf(docUrl) && (
							<PreviewImage src={docUrl} alt="Proof of Request" />
						)}
						{!docUrl && (
							<>
								<FileTextOutlined style={{ fontSize: '48px' }} />
								<span>No document available</span>
							</>
						)}
					</PreviewPane>

					{isPending && (
						<FooterButtons>
							<ActionButton
								borderColor="#FF3B30"
								onClick={() => setShowRejectModal(true)}
								disabled={loading}
							>
								Reject
							</ActionButton>
							<ActionButton
								borderColor="#389E0E"
								onClick={handleApprove}
								disabled={loading}
							>
								Approve
							</ActionButton>
						</FooterButtons>
					)}
				</ModalWrapper>
			</Modal>

			{/* Reason for Rejection modal */}
			<Modal
				open={showRejectModal}
				width={520}
				onCancel={() => setShowRejectModal(false)}
				footer={null}
				closable={false}
				centered
				bodyStyle={{ padding: 0 }}
			>
				<RejectModalContent>
					<RejectModalTitle>Reason for Rejection</RejectModalTitle>
					<RejectLabel>Reason</RejectLabel>
					<TextArea
						rows={3}
						placeholder="Enter your comments here"
						value={rejectionReason}
						onChange={(e) => setRejectionReason(e.target.value)}
					/>
					<RejectModalButtons>
						<CancelButton onClick={() => setShowRejectModal(false)}>
							Cancel
						</CancelButton>
						<SubmitButton onClick={handleRejectSubmit} disabled={loading}>
							{loading ? 'Submitting...' : 'Submit'}
						</SubmitButton>
					</RejectModalButtons>
				</RejectModalContent>
			</Modal>
		</>
	);
}

PoRViewerModal.propTypes = {
	porId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	documentKey: PropTypes.string,
	onActionComplete: PropTypes.func,
	data: PropTypes.shape({
		display_id: PropTypes.string,
		trip_code: PropTypes.string,
		project_name: PropTypes.string,
		vendor_name: PropTypes.string,
		vehicle_number: PropTypes.string,
		status: PropTypes.string,
		submission_number: PropTypes.number,
		rejection_reason: PropTypes.string,
	}),
};
